import React from "react";
import { Link as RouterLink, useParams } from "react-router-dom";
import { injectGlobal } from "@emotion/css";
import { Alert, AlertTitle, Breadcrumbs, Button, Link, Typography } from "@mui/material";
import useAsyncRequest from "hooks/useAsyncRequest";
import { LoadablePage } from "shared/LoadablePage";
import { DetailsPageLayout } from "components/DetailsPageLayout/DetailsPageLayout";
import { PageContent, pageContentMaxWidth } from "components/PageContent/PageContent";
import { PageHeaderBar } from "components/PageHeaderBar/PageHeaderBar";
import { LicenceGlyph } from "areas/orders/components/chrome/LicenceGlyph";
import { quoteSheetClassName, QuoteSheet } from "areas/orders/document/QuoteSheet";
import { orderRoutes } from "areas/orders/order-routes";
import { getOrderDocument } from "areas/orders/services/orderService";
import { isOrderNotFound } from "areas/orders/utils/orderErrors";

// Why: `visibility` rather than `display`, so the sheet keeps the layout it was measured with.
// The page takes no margin because the sheet already carries the template's 0.5in padding.
injectGlobal`
  @media print {
    @page {
      size: letter;
      margin: 0;
    }
    body * {
      visibility: hidden;
    }
    .${quoteSheetClassName},
    .${quoteSheetClassName} * {
      visibility: visible;
    }
    .${quoteSheetClassName} {
      position: absolute;
      left: 0;
      top: 0;
      width: 100%;
      box-shadow: none;
      /* Or the price table's header row prints white and loses the band the template gives it. */
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
  }
`;

function DocumentBreadcrumbs({ odNumber }: { odNumber: string }) {
  return (
    <Breadcrumbs sx={{ pb: 2, typography: "body2" }}>
      <Link component={RouterLink} to={orderRoutes.root}>
        Orders
      </Link>
      <Typography variant="body2" color="text.secondary">
        {odNumber}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        document
      </Typography>
    </Breadcrumbs>
  );
}

function DocumentNotFound({ odNumber }: { odNumber: string }) {
  return (
    <PageContent maxWidth={pageContentMaxWidth}>
      <DocumentBreadcrumbs odNumber={odNumber} />
      <Alert severity="warning">
        <AlertTitle>{`No order found for ${odNumber}`}</AlertTitle>
        <Typography variant="body2" component="div">
          Check the OD number, or pick an order from the list.
        </Typography>
        <Link component={RouterLink} to={orderRoutes.root} sx={{ mt: 1, display: "inline-block" }}>
          Back to Orders
        </Link>
      </Alert>
    </PageContent>
  );
}

export function QuoteDocumentPage() {
  const { odNumber } = useParams<{ odNumber: string }>();
  const resolvedOdNumber = odNumber || "UNKNOWN_ORDER";

  const { data: content, loading, error } = useAsyncRequest(getOrderDocument, resolvedOdNumber);

  if (isOrderNotFound(error)) {
    return <DocumentNotFound odNumber={resolvedOdNumber} />;
  }

  return (
    <LoadablePage loading={loading && !content} error={error}>
      {content && (
        <>
          <PageHeaderBar maxWidth={pageContentMaxWidth}>
            <DocumentBreadcrumbs odNumber={content.odNumber} />
            <DetailsPageLayout
              pageName={content.odNumber}
              pageNameSx={{ fontWeight: 500 }}
              titleIcon={<LicenceGlyph odNumber={content.odNumber} />}
              actions={[
                <Button key="print" variant="contained" onClick={() => window.print()}>
                  Print
                </Button>,
                <Button
                  key="open"
                  variant="outlined"
                  component={RouterLink}
                  to={orderRoutes.detail(content.odNumber).root}
                >
                  Open in builder
                </Button>,
              ]}
            />
          </PageHeaderBar>
          <PageContent maxWidth={pageContentMaxWidth}>
            <QuoteSheet content={content} />
          </PageContent>
        </>
      )}
    </LoadablePage>
  );
}
